import { useNode } from "@craftjs/core";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "styled-components";
import useApplyGlobalSettings, {
  CODEBLOCK_TYPES,
} from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { toggleSegmentSettings } from "../../../../redux/actions/segmentSettings";
import { updateContainerStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import {
  applyOpacity,
  isRGBA,
  sliderToOpacity
} from "../../../../utils/helpers";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import { ContainerSettingsComp } from "./Settings";

export const Container = ({
  background,
  height,
  radius,
  fullWidth,
  spacing,
  margin,
  isApplyPaddingOnAllSides,
  isApplyMarginOnAllSides,
  allSideSpacing,
  allSideMargin,
  opacity,
  border,
  dropShadowColor,
  children,
  ...props
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();

  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { container: containerProps } = useSelector((state) => state.codeblockStyles);
  const {getPaletteColor} = usePaletteColor()

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        props.background = props.background || containerProps.background;
        props.height = props.height || containerProps.height;
        props.radius = props.radius || containerProps.radius;
        props.fullWidth = props.fullWidth || containerProps.fullWidth;
        props.spacing = props.spacing || { ...containerProps.spacing };
        props.margin = props.margin || { ...containerProps.margin };
        props.opacity = props.opacity || containerProps.opacity;
        props.border = props.border || containerProps.border;
        props.dropShadowColor =
          props.dropShadowColor || containerProps.dropShadowColor;
      });
    }, 5);

    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, []);

  // Reusable hook which applies global settings in each codeblock
  //some keys are different for container codeblock

  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Container);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!background) {
        setProp((props) => {
          props.background = containerProps.background || "#ffffff";
        });
      }

      if (!border?.color) {
        setProp((props) => {
          props.border.color =
            containerProps.border.color || "suggested_color_0";
        });
      }
    }, 10);

    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, [setProp, border, background, containerProps, selectedSegmentColor]);

  const paddingStyle = `${spacing?.top}px ${spacing?.right}px ${spacing?.bottom}px ${spacing?.left}px`


  const marginStyle = `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`

  const hasChildren = React.Children.count(children) > 0;

  const StyledPaper = styled.div``;

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <StyledPaper
          {...props}
          style={{
            background: `${applyOpacity(
              getPaletteColor(background),
              sliderToOpacity(opacity)
            )}`,
            minHeight: hasChildren ? "auto" : height ? `${height}vh` : "10vh",
            boxSizing: "border-box",
            padding: paddingStyle,
            borderRadius: `${radius}px`,
            borderColor:
              isRGBA(applyOpacity(getPaletteColor(border?.color), 1)) &&
              applyOpacity(getPaletteColor(border?.color), 1),
            borderImage:
              !isRGBA(applyOpacity(getPaletteColor(border?.color), 1)) &&
              `${applyOpacity(getPaletteColor(border?.color), 1)} 30`,
            borderWidth: `${border?.width}px`,
            borderStyle: "solid",
            boxShadow: dropShadowColor
              ? `0px 4px 12px ${getPaletteColor(dropShadowColor)}`
              : "none",
            margin: marginStyle,
          }}
        >
          {children}
        </StyledPaper>
      </BlockIndicator>
    </div>
  );
};

export const ContainerSettings = () => {
  const {
    background,
    padding,
    radius,
    height,
    fullWidth,
    spacing,
    margin,
    isApplyMarginOnAllSides,
    isApplyPaddingOnAllSides,
    allSideSpacing,
    allSideMargin,
    opacity,
    border,
    dropShadowColor,
    actions: { setProp },
  } = useNode((node) => ({
    background: node.data.props.background,
    padding: node.data.props.padding,
    height: node.data.props.height,
    radius: node.data.props.radius,
    fullWidth: node.data.props.fullWidth,
    spacing: node.data.props.spacing,
    margin: node.data.props.margin,
    isApplyMarginOnAllSides: node.data.props.isApplyMarginOnAllSides,
    isApplyPaddingOnAllSides: node.data.props.isApplyPaddingOnAllSides,
    allSideSpacing: node.data.props.allSideSpacing,
    allSideMargin: node.data.props.allSideMargin,
    opacity: node.data.props.opacity,
    border: node.data.props.border,
    dropShadowColor: node.data.props.dropShadowColor,
  }));
  const dispatch = useDispatch();

  const handleChange = (type, sideOrValue, value) => {
    dispatch(toggleSegmentSettings(false));
    setProp((props) => {
      // Clone the props to avoid mutating Proxies
      const newProps = {
        ...props,
        border: { ...props.border }, // Convert border Proxy to plain object
        margin: { ...props.margin }, // Convert margin Proxy to plain object
        spacing: { ...props.spacing }, // Convert spacing Proxy to plain object
      };

      // Set isFirstContainer to false for every change
      props.isFirstContainer = false;
      newProps.isFirstContainer = false;

      switch (type) {
        case "fullWidth":
          if (sideOrValue !== "full") {
            props.margin.top = 10;
            props.margin.left = 20;
            props.margin.right = 20;
            newProps.margin.top = 10;
            newProps.margin.left = 20;
            newProps.margin.right = 20;
          } else {
            props.margin.top = 0;
            props.margin.left = 0;
            props.margin.right = 0;
            newProps.margin.top = 0;
            newProps.margin.left = 0;
            newProps.margin.right = 0;
          }
          props.fullWidth = sideOrValue === "full";

          newProps.fullWidth = sideOrValue === "full";
          break;
        case "radius":
          newProps.radius = sideOrValue;
          props.radius = sideOrValue;
          break;
        case "spacing":
          if (value >= 0 && value <= 100) {
            newProps.spacing[sideOrValue] = value;
            props.spacing[sideOrValue] = value;
          }
          break;
        case "margin":
          if (value >= 0 && value <= 100) {
            newProps.margin[sideOrValue] = value;
            props.margin[sideOrValue] = value;
          }
          break;
        case "marginToggle":
          newProps.isApplyMarginOnAllSides = !newProps.isApplyMarginOnAllSides;
          props.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;

          break;
        case "marginChange":
          newProps.allSideMargin = value;
          props.allSideMargin = value;

          break;
        case "paddingToggle":
          newProps.isApplyPaddingOnAllSides =
            !newProps.isApplyPaddingOnAllSides;
          props.isApplyPaddingOnAllSides = !props.isApplyPaddingOnAllSides;

          break;
        case "paddingChange":
          newProps.allSideSpacing = value;
          props.allSideSpacing = value;

          break;
        case "addColor":
          newProps.background = value;
          props.background = value;

          break;
        case "resetToDefault":
          newProps.background = "#ffffff";
          props.background = "#ffffff";

          break;
        case "transparency":
          newProps.opacity = value;
          props.opacity = value;

          break;
        case "borderChange":
          newProps.border[sideOrValue] = value;
          props.border[sideOrValue] = value;
          break;
        case "resetBorderColor":
          props.border.color = "#ffffff";
          newProps.border.color = "#ffffff";
          break;
        case "wallpaperTransparency":
          props.wallpaperTransparency = value;
          newProps.wallpaperTransparency = value;
          break;
        case "wallpaperResizing":
          props.wallpaperResizing = value;
          newProps.wallpaperResizing = value;
          break;
        case "addWallpaperOverlayColor":
          props.wallpaperOverlayColor = value;
          newProps.wallpaperOverlayColor = value;
          break;
        case "resetWallpaperOverlayColor":
          props.wallpaperOverlayColor = "#ffffff";
          newProps.wallpaperOverlayColor = "#ffffff";
          break;
        case "addBgWallpaper":
          props.bgWallpaper = value;
          props.isPattern = sideOrValue;
          newProps.bgWallpaper = value;
          newProps.isPattern = sideOrValue;
          break;
        case "dropShadowColor":
          props.dropShadowColor = value;
          newProps.dropShadowColor = value;
          break;
        case "resetShadow":
          props.dropShadowColor = null;
          newProps.dropShadowColor = null;
          break;
        default:
          break;
      }

      // Dispatch the updated props as plain objects
      dispatch(updateContainerStyles(newProps));
    });
  };

  return (
    <ContainerSettingsComp
      setProp={setProp}
      background={background}
      padding={padding}
      radius={radius}
      fullWidth={fullWidth}
      handleWidthChange={(value) => handleChange("fullWidth", value)}
      handleBorderRadius={(value) => handleChange("radius", value)}
      handleSpacingChange={(side, value) =>
        handleChange("spacing", side, value)
      }
      spacing={spacing}
      handleMarginChange={(side, value) => handleChange("margin", side, value)}
      margin={margin}
      isApplyMarginOnAllSides={isApplyMarginOnAllSides}
      isApplyPaddingOnAllSides={isApplyPaddingOnAllSides}
      handleMarginCheckBox={() => handleChange("marginToggle")}
      allSideMargin={allSideMargin}
      handleSliderMarginChange={(value) =>
        handleChange("marginChange", null, value)
      }
      handlePaddingCheckBox={() => handleChange("paddingToggle")}
      allSideSpacing={allSideSpacing}
      handleSliderPaddingChange={(value) =>
        handleChange("paddingChange", null, value)
      }
      handleChangeBackgroundColor={(value) =>
        handleChange("addColor", null, value)
      }
      resetBackgroundColor={() => handleChange("resetToDefault")}
      transparency={opacity}
      handleTransparencyChange={(value) =>
        handleChange("transparency", null, value)
      }
      borderSelectedColor={border?.color}
      handleBorderChange={(side, value) =>
        handleChange("borderChange", side, value)
      }
      borderWidth={border?.width}
      resetBorderColor={() => handleChange("resetBorderColor", null, null)}
      dropShadowColor={dropShadowColor}
      handleDropShadowChange={(val) =>
        handleChange("dropShadowColor", null, val)
      }
      handleDropShadowReset={() => handleChange("resetShadow", null, null)}
    />
  );
};

export const ContainerDefaultProps = {};

Container.craft = {
  props: ContainerDefaultProps,
  related: {
    settings: ContainerSettings,
  },
};
